import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Search, Bell, ChevronDown, LogOut, User as UserIcon, Building2 } from 'lucide-react';
import { cn } from '../lib/utils';

export default function Topbar() {
  const { user, logout, company, setCompany, alerts } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const criticalCount = alerts.filter((a) => a.severity === 'critical' && a.status !== 'resolved').length;

  const handleSearch = (e) => {
    if (e.key === 'Enter' && q.trim()) {
      navigate(`/devices?q=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <header
      className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center px-6 gap-4"
      data-testid="topbar"
    >
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-slate-400" />
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger className="h-9 w-[220px] border-slate-200" data-testid="company-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="JB Heating & Air">JB Heating & Air</SelectItem>
            <SelectItem value="JB Commercial Services">JB Commercial Services</SelectItem>
            <SelectItem value="JB West Region">JB West Region</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 max-w-xl relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleSearch}
          placeholder="Search customers, devices, alerts, addresses..."
          className="pl-9 h-9 bg-slate-50 border-slate-200 focus-visible:bg-white"
          data-testid="global-search"
        />
      </div>

      <button
        onClick={() => navigate('/alerts')}
        className="relative h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center transition-colors"
        data-testid="topbar-alerts-btn"
        aria-label="Alerts"
      >
        <Bell className="h-4 w-4 text-slate-600" />
        {criticalCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
            {criticalCount}
          </span>
        )}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-9 pl-1 pr-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-2 transition-colors"
            data-testid="user-menu-trigger"
          >
            <div className={cn('h-7 w-7 rounded-md bg-slate-900 text-white text-xs font-semibold flex items-center justify-center')}>
              {user?.initials || 'JB'}
            </div>
            <div className="hidden md:block text-left leading-tight">
              <div className="text-[13px] font-medium text-slate-900">{user?.name || 'User'}</div>
              <div className="text-[10px] text-slate-500">{user?.role}</div>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-profile">
            <UserIcon className="h-4 w-4 mr-2" /> Profile & Account
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
            <Building2 className="h-4 w-4 mr-2" /> Company Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate('/login');
            }}
            data-testid="menu-logout"
            className="text-red-600 focus:text-red-600"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
